/* review-workspace-diff.jsx — real-render visual comparison for the review
   workspace. Admitted build/source artifacts remain authoritative; a local
   image is offered only when this build has no admitted source render. */

const RW_DIFF_MODES = [
  { key: 'side', label: 'Side by side', help: 'Both at once — best for spotting a missing block' },
  { key: 'swipe', label: 'Swipe', help: 'Drag the divider — best for checking one edge or alignment' },
  { key: 'fade', label: 'Fade', help: 'Cross-mix the two — best for drift across a whole block' },
  { key: 'difference', label: 'Difference', help: 'Anything not black is a discrepancy' },
];
const RW_DIFF_CERTIFIED = new Map();

function rwDiffLayer1(value, jobId, seen) {
  if (!value || typeof value !== 'object') return null;
  const visited = seen || new Set();
  if (visited.has(value)) return null;
  visited.add(value);
  if (Array.isArray(value.checks) || value.schema_version === 'sabre-qa-report/1.0') return value;
  const keys = [jobId, 'report', 'qa', 'layer1', 'data'];
  for (const key of keys) {
    if (key && value[key]) {
      const found = rwDiffLayer1(value[key], jobId, visited);
      if (found) return found;
    }
  }
  return null;
}

function rwDiffLegacyMarkdown(text) {
  const lines = String(text || '').split(/\r?\n/);
  const named = lines.find((line) => /render_viewports/i.test(line));
  if (named) {
    const match = named.match(/render_viewports\*{0,2}\s*(?:—|:|-)\s*(.+)$/i);
    return { checks: [{ name: 'render_viewports', ok: /❌|\bFAIL\b/i.test(named) ? false : /✅|\bPASS\b|\bOK\b/i.test(named) ? true : undefined, detail: match ? match[1].trim() : named.trim() }] };
  }
  const widthNote = lines.find((line) => /(?:320|600)/.test(line) && /viewport|width|overflow|reflow/i.test(line));
  return widthNote ? { viewport_note: widthNote.trim() } : null;
}

async function rwDiffReadLayer1(job, isLive) {
  if (!isLive || !job || !job.id) return null;
  if (job.engine === 'sabre' && window.RW_SABRE_ARTIFACTS === false) return null;
  const seam = window.RwSabre && window.RwSabre.layer1;
  if (seam) {
    try {
      const shared = await Promise.resolve(typeof seam === 'function' ? seam(job.id, job) : seam);
      const report = rwDiffLayer1(shared, job.id);
      if (report) return report;
    } catch (_) {}
  }
  if (!RW_DIFF_CERTIFIED.has(job.id)) {
    const request = (async () => {
      if (!(window.GooseAPI && window.GooseAPI.fetchArtifactText)) return null;
      try {
        const json = job.engine === 'sabre';
        const result = await window.GooseAPI.fetchArtifactText(job.id, json ? '04_qa/qa-report.json' : '04_qa/qa-report.md');
        if (!result || !result.text) return null;
        if (!json) return rwDiffLegacyMarkdown(result.text);
        try {return JSON.parse(result.text);} catch (_) {return { viewport_note: result.text };}
      } catch (_) {return null;}
    })();
    RW_DIFF_CERTIFIED.set(job.id, request);
  }
  return RW_DIFF_CERTIFIED.get(job.id);
}

function rwDiffViewportCheck(report) {
  if (!report || typeof report !== 'object') return null;
  if (Array.isArray(report.checks)) {
    const row = report.checks.find((check) => check && check.name === 'render_viewports');
    if (row) return row;
  } else if (report.checks && report.checks.render_viewports) {
    const row = report.checks.render_viewports;
    return typeof row === 'string' ? { detail: row } : row;
  }
  const note = report.render_viewports || report.viewport_note || report.width_note || report.width;
  if (typeof note === 'string' && /320|600|viewport|width|overflow|reflow/i.test(note)) return { detail: note };
  if (note && typeof note === 'object') return note;
  return null;
}

function rwDiffViewportPart(width, raw, passed) {
  let detail = String(raw || '').replace(/^\s*(?:600|320)(?:px)?\s*:\s*/i, '').trim();
  const richOverflow = detail.match(/([a-z][\w.-]*)\s+overflows?\s+(?:the\s+)?container\s+by\s+(\d+)\s*px/i);
  if (richOverflow) return width + ': overflow ' + richOverflow[2] + 'px — ' + richOverflow[1];
  const regionCount = detail.match(/(\d+)\s+regions?\s+overflow/i);
  if (regionCount) return width + ': ' + regionCount[1] + ' region' + (regionCount[1] === '1' ? '' : 's') + ' overflow';
  const didNotReflow = detail.match(/did\s+not\s+reflow[\s\S]*?width\s+(\d+)\s*>\s*(\d+)/i);
  if (didNotReflow) return width + ': failed — did not reflow (' + didNotReflow[1] + 'px render)';
  if (/^(?:ok\b|clean\b)/i.test(detail) && !/fail|overflow|not\s+reflow/i.test(detail)) return width + ': clean';
  detail = detail.replace(/^FAIL\s*/i, '').trim();
  if (!detail && passed) return width + ': clean';
  return width + ': ' + (detail || 'failed — detail unavailable');
}

function rwDiffCertified(report) {
  const check = rwDiffViewportCheck(report);
  if (!check) return { status: 'not-asserted', text: 'not asserted' };
  const detail = typeof check === 'string' ? check : String(check.detail || check.note || '');
  const pieces = {};
  const matcher = /(?:^|[;·\n])\s*(600|320)(?:px)?\s*:\s*([^;·\n]+)/gi;
  let match;
  while ((match = matcher.exec(detail))) pieces[match[1]] = rwDiffViewportPart(match[1], match[2], check.ok === true);
  if (!pieces['600'] && !pieces['320']) {
    if (check.ok === true) {
      pieces['600'] = '600: clean';
      pieces['320'] = '320: clean';
    } else if (detail) {
      return { status: 'asserted', text: detail.trim() + ' (Last certified QA)' };
    } else {
      return { status: 'not-asserted', text: 'not asserted' };
    }
  }
  if (!pieces['600']) pieces['600'] = check.ok === true ? '600: clean' : '600: not recorded';
  if (!pieces['320']) pieces['320'] = check.ok === true ? '320: clean' : '320: not recorded';
  return { status: 'asserted', text: pieces['600'] + ' · ' + pieces['320'] + ' (Last certified QA)' };
}

function useRwDiffCertified(job, isLive, sessionArtifacts, revisionQa, width) {
  const [certified, setCertified] = React.useState(() => isLive
    ? { status: 'loading', text: 'checking certified QA…' }
    : { status: 'not-asserted', text: 'not asserted' });
  React.useEffect(() => {
    let alive = true;
    if (sessionArtifacts) {
      // Never fall through to the mutable original-job report for an edited revision.
      const current = revisionQa?.state === 'current';
      const measured = revisionQa?.receipt?.renders?.[String(width)]?.width_px;
      // width_px identifies the captured viewport, not the content's overflow.
      const axis = width === 320 ? 'overflow_mobile' : width === 600 ? 'overflow_desktop' : null;
      const verdict = axis && revisionQa?.receipt?.axes?.[axis];
      const renderBound = current && measured === width && !!sessionArtifacts.renders?.[String(width)];
      const bound = renderBound && typeof verdict === 'boolean';
      setCertified({status: bound ? 'revision-bound' : 'not-asserted', text: bound
        ? `${revisionQa.revision} · ${width}px: ${verdict ? 'no width overflow' : 'width overflow detected'} (${measured}px render)`
        : renderBound && !axis ? `${width}px render (no floor axis)`
        : current ? `${width}px: not measured`
        : `${revisionQa?.label || 'QA unavailable'} · renders are not a draft certification`});
      return () => {alive = false;};
    }
    setCertified(isLive ? { status: 'loading', text: 'checking certified QA…' } : { status: 'not-asserted', text: 'not asserted' });
    rwDiffReadLayer1(job, isLive).then((report) => {if (alive) setCertified(rwDiffCertified(report));});
    return () => {alive = false;};
  }, [isLive, job && job.id, sessionArtifacts, revisionQa?.state, revisionQa?.revision, revisionQa?.label, revisionQa?.receipt, width]);
  return certified;
}

function RwRenderWidthEvidence({ job, isLive, sessionArtifacts, revisionQa, width, draftDirty, stagedPreview }) {
  const result = useRwDiffCertified(job, isLive, sessionArtifacts, revisionQa, width);
  if (draftDirty || stagedPreview) return null;
  return <details className="w-render-evidence" data-certified-viewport={result.status}
    title="Retained server measurement; not a measurement of an unsaved or staged draft">
    <summary><BI.review size={11} /> Render evidence</summary><span data-certified-viewport-value="1">{result.text}</span>
  </details>;
}

function RwDiffSourceDrop({ setSrcImg }) {
  const input = React.useRef(null);
  const [over, setOver] = React.useState(false);
  const take = (file) => {
    if (!file || !/^image\//.test(file.type || '')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => setSrcImg && setSrcImg({ url: reader.result, name: file.name, w: image.naturalWidth, h: image.naturalHeight });
      image.src = reader.result;
    };
    reader.readAsDataURL(file);
  };
  const choose = () => input.current && input.current.click();
  return (
    <div className={'w-src' + (over ? ' over' : '')} role="button" tabIndex="0" data-source-drop="fallback"
      onDragOver={(event) => {event.preventDefault();setOver(true);}}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {event.preventDefault();setOver(false);take(event.dataTransfer.files && event.dataTransfer.files[0]);}}
      onClick={choose}
      onKeyDown={(event) => {if (event.key === 'Enter' || event.key === ' ') {event.preventDefault();choose();}}}>
      <BI.image size={22} />
      <span><b>Drop the source design</b><br />PDF export, Figma frame or flat PNG — it becomes the comparison baseline</span>
      <input ref={input} type="file" accept="image/*" hidden data-source-file="fallback"
        onChange={(event) => {take(event.target.files && event.target.files[0]);event.target.value = '';}} />
    </div>
  );
}

function RwOriginalBuiltRender({ url, bare, width }) {
  const [state,setState] = React.useState('loading');
  React.useEffect(()=>setState('loading'),[url]);
  return <figure className="rs" data-render-slot="render-built">
    <div className="rs-frame">
      {url && <img className="rs-img" src={url} alt={`Original build render · ${width}px`} style={{display:state==='ready'?'block':'none',width:'100%',height:'auto'}} onLoad={()=>setState('ready')} onError={()=>setState('missing')}/>}
      {(!url || state==='missing') ? <div className="w-bar-note" data-original-render-missing>Built render not generated yet — open build details to render it.</div>
        : state==='loading' ? <div className="w-bar-note">Loading original build render…</div> : null}
    </div>
    {!bare && <figcaption className="rs-cap">Original build render · {width}px</figcaption>}
  </figure>;
}

function RwDiffBuiltSlot({ device, c, isLive, job, bare, sessionArtifacts, width }) {
  const SessionSlot = window.RwSessionRenderSlot;
  const url = sessionArtifacts ? sessionArtifacts.renders?.[String(width)] || null
    : isLive && window.GooseAPI?.artifactUrl ? window.GooseAPI.artifactUrl(job.id, device === 'mobile' ? '04_qa/mobile-375px.png' : '04_qa/desktop-600px.png') : null;
  return <div data-built-layer="1" style={{ width: '100%', display: 'grid', placeItems: 'center' }}>
    {sessionArtifacts ? <SessionSlot device={device} c={{...c,width}} id="render-built" label={`Retained render · ${width}px`} bare={bare} url={url} />
      : <RwOriginalBuiltRender url={url} bare={bare} width={width}/>}
  </div>;
}

function RwDiffSourceLayer({ device, c, isLive, job, bare,
  sourceStatus, setSourceStatus, srcImg, setSrcImg, sessionArtifacts }) {
  const admitted = sourceStatus === 'ready' && !srcImg;
  const SessionSlot = window.RwSessionRenderSlot;
  return (
    <div data-source-state={srcImg ? 'local' : sourceStatus} style={{ width: '100%', display: 'grid', placeItems: 'center' }}>
      <div data-source-admitted="1" style={{ gridArea: '1 / 1', width: '100%', display: 'grid', placeItems: 'center', visibility: admitted ? 'visible' : 'hidden' }}
        onLoadCapture={(event) => {
          if (event.target && event.target.matches && event.target.matches('figure[data-render-slot="render-source"] img.rs-img')) setSourceStatus('ready');
        }}
        onErrorCapture={(event) => {
          if (event.target && event.target.matches && event.target.matches('figure[data-render-slot="render-source"] img.rs-img')) setSourceStatus('missing');
        }}>
        {sessionArtifacts ? <SessionSlot device={device} c={c} id="render-source" label="Source design" bare={bare} url={sessionArtifacts.source_render || null} />
          : <WsRenderSlot key={job.id + ':' + device} device={device} c={c} isLive={isLive} job={job} id="render-source" label="Source design" bare={bare} tint={false} />}
      </div>
      {srcImg && <img src={srcImg.url} alt={srcImg.name || 'Local source fallback'} className="w-src-img" data-source-fallback-img="1"
        style={{ gridArea: '1 / 1', alignSelf: 'start' }} />}
      {!srcImg && sourceStatus === 'checking' && <div className="w-bar-note" data-source-loading="1"
        style={{ gridArea: '1 / 1', minHeight: 220, display: 'grid', placeItems: 'center', textAlign: 'center' }}>Checking admitted source render…</div>}
      {!srcImg && sourceStatus === 'missing' && <div style={{ gridArea: '1 / 1', width: '100%' }}><RwDiffSourceDrop setSrcImg={setSrcImg} /></div>}
    </div>
  );
}

function RwDifferenceCanvas({ rootRef, canvasRef, available, redrawKey, hidden, onBlocked }) {
  const [ready, setReady] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas || !available) {setReady(false);return;}
    const built = root.querySelector('figure[data-render-slot="render-built"] img.rs-img');
    const source = root.querySelector('[data-source-fallback-img="1"]') || root.querySelector('figure[data-render-slot="render-source"] img.rs-img');
    if (!built || !source) {setReady(false);return;}
    setReady(false);
    setFailed(false);
    let alive = true;
    const fail = () => {if (alive) {setReady(false);setFailed(true);}};
    const draw = () => {
      if (!alive || !built.complete || !source.complete) return;
      if (!built.naturalWidth || !source.naturalWidth) {fail();return;}
      try {
        const width = Math.max(1, Math.min(1200, built.naturalWidth));
        const builtHeight = Math.round(built.naturalHeight * width / built.naturalWidth);
        const sourceHeight = Math.round(source.naturalHeight * width / source.naturalWidth);
        const height = Math.max(1, Math.min(16384, Math.max(builtHeight, sourceHeight)));
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.globalCompositeOperation = 'source-over';
        context.fillStyle = '#000';
        context.fillRect(0, 0, width, height);
        context.drawImage(source, 0, 0, width, sourceHeight);
        context.globalCompositeOperation = 'difference';
        context.drawImage(built, 0, 0, width, builtHeight);
        context.globalCompositeOperation = 'source-over';
        context.getImageData(0, 0, 1, 1); // SecurityError is the taint tripwire.
        setReady(true);
      } catch (_) {
        setReady(false);
        onBlocked();
      }
    };
    built.addEventListener('load', draw);
    source.addEventListener('load', draw);
    built.addEventListener('error', fail);
    source.addEventListener('error', fail);
    const frame = requestAnimationFrame(draw);
    return () => {
      alive = false;
      cancelAnimationFrame(frame);
      built.removeEventListener('load', draw);
      source.removeEventListener('load', draw);
      built.removeEventListener('error', fail);
      source.removeEventListener('error', fail);
    };
  }, [available, redrawKey]);
  return <React.Fragment><canvas ref={canvasRef} data-difference-canvas="1" data-difference-state={ready ? 'ready' : failed ? 'failed' : 'waiting'}
    aria-label="Pixel difference between source and built renders" style={{ gridArea: '1 / 1', zIndex: 3, width: '100%', height: 'auto', maxWidth: '100%', background: '#000', borderRadius: 5, display: ready && !hidden ? 'block' : 'none' }} />
    {failed && <div className="w-vd-need" data-difference-load-error>Retained render could not be loaded. Reload or rerun QA; no substitute comparison is shown.</div>}
  </React.Fragment>;
}

function RwDiffTabs({ mode, setMode }) {
  return <div className="w-seg" data-diff-mode={mode}>
    {RW_DIFF_MODES.map((entry, index) => <button key={entry.key} className={mode === entry.key ? 'on' : ''}
      aria-pressed={mode === entry.key} title={entry.help + ' · ' + (index + 1)}
      onClick={() => setMode(entry.key)}>{entry.label}</button>)}
  </div>;
}

function RwDiffLightbox({ children, caption, onClose }) {
  React.useEffect(() => {
    const key = event => {if (event.key === 'Escape') {event.stopImmediatePropagation();onClose();}};
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [onClose]);
  return ReactDOM.createPortal(<div className="w-lb w-review-theme" role="dialog" aria-modal="true" aria-label={caption} onClick={onClose}>
    <div className="w-lb-in" onClick={event => event.stopPropagation()}>{children}</div>
    <div className="w-lb-cap">{caption}</div>
    <div className="w-lb-hint">click outside · esc to close</div>
  </div>, document.body);
}

function RwVisualDiffSurface(props) {
  const job = props.job || { id: '', name: 'Build', source: 'pdf' };
  const c = props.c || { color: '#3B3C43', accent: '#f4c542', width: 600 };
  const vp = props.vp || { mode: 'desktop', desktop: 600, mobile: 375 };
  const device = vp.mode === 'mobile' ? 'mobile' : 'desktop';
  const width = device === 'mobile' ? (+vp.mobile || 375) : (+vp.desktop || 600);
  const { mode, setMode, blend, setBlend, split, setSplit } = props.controls;
  const [flip, setFlip] = React.useState(false);
  const [lightbox, setLightbox] = React.useState(null);
  const [differenceNote, setDifferenceNote] = React.useState('');
  const surfaceRef = React.useRef(null);
  const stackRef = React.useRef(null);
  const differenceRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const [surfaceWidth, setSurfaceWidth] = React.useState(0);
  const sourcePath = (job.source || 'pdf') === 'figma' ? '_figma/screenshot.png' : '_pdf/screenshot.png';
  const sourceUrl = props.sessionArtifacts ? props.sessionArtifacts.source_render || null
    : props.isLive && window.GooseAPI?.artifactUrl ? window.GooseAPI.artifactUrl(job.id, sourcePath) : null;
  const [sourceStatus, setSourceStatus] = React.useState(() => sourceUrl ? 'checking' : 'missing');
  const sourceAvailable = sourceStatus === 'ready' || !!props.srcImg;
  const stackWidth = Math.min(width, Math.max(240, (surfaceWidth || props.midWidth || 900) - 64));
  const retainedUrl = props.sessionArtifacts?.renders?.[String(width)];
  const unrendered = props.draftDirty || props.stagedPreview;
  const exactRetained = props.sessionArtifacts ? !!retainedUrl : (device === 'desktop' ? width === 600 : width === 375);
  const retainedWidths = Object.keys(props.sessionArtifacts?.renders || {}).filter(value => /^\d+$/.test(value) && props.sessionArtifacts.renders[value]).map(Number).sort((a,b) => a-b);
  const differenceWhy = unrendered ? 'Difference unavailable for this unrendered draft. Apply changes and rerun QA to compare retained pixels.'
    : !exactRetained ? 'Difference unavailable at ' + width + 'px. No retained render exists for this width.' : '';
  const diffAvailable = sourceAvailable && !differenceWhy;
  const B = props.B;

  React.useLayoutEffect(() => {
    const element = surfaceRef.current;
    if (!element) return;
    const measure = () => setSurfaceWidth(element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);observer.observe(element);
    return () => observer.disconnect();
  }, []);
  React.useEffect(() => {setSourceStatus(sourceUrl ? 'checking' : 'missing');}, [sourceUrl]);
  React.useEffect(() => {
    const down = event => {
      if (lightbox || /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '') ||
        document.activeElement?.isContentEditable || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key.toLowerCase() === 'f' && sourceAvailable) {event.preventDefault();setFlip(true);}
      const index = ['1', '2', '3', '4'].indexOf(event.key);
      if (index >= 0) {event.preventDefault();setMode(RW_DIFF_MODES[index].key);}
      if (mode === 'swipe' && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        event.preventDefault();
        setSplit(value => Math.max(0, Math.min(100, value + (event.key === 'ArrowRight' ? 1 : -1) * (event.shiftKey ? 5 : 1))));
      }
    };
    const up = event => {if (event.key.toLowerCase() === 'f') setFlip(false);};
    const release = () => setFlip(false);
    window.addEventListener('keydown', down);window.addEventListener('keyup', up);window.addEventListener('blur', release);
    return () => {window.removeEventListener('keydown', down);window.removeEventListener('keyup', up);window.removeEventListener('blur', release);};
  }, [mode, sourceAvailable, lightbox]);

  const retained = () => <RwDiffBuiltSlot device={device} c={c} isLive={props.isLive} job={job} bare
    sessionArtifacts={props.sessionArtifacts} width={width} />;
  const built = (fit = true) => props.liveHtml ? <div data-built-layer="live" style={{width: fit ? '100%' : width, maxWidth: '100%'}}>
    <BeFrame html={props.liveHtml} sel={B?.previewSel || B?.sel}
      onSel={id => B?.setSel(B.fromPreview ? B.fromPreview(id) : id)} width={width} fit={fit}
      patched={props.patched} resizeKey={surfaceWidth + ':' + mode} artifactUrl={props.artifactUrl} isolated={props.isolated} />
    </div> : retained();
  const source = () => <RwDiffSourceLayer device={device} c={{...c,width}} isLive={props.isLive} job={job} bare
    sessionArtifacts={props.sessionArtifacts}
    sourceStatus={sourceStatus} setSourceStatus={setSourceStatus} srcImg={props.srcImg} setSrcImg={props.setSrcImg} />;
  const shownBlend = flip ? 100 - blend : blend;
  const shownSplit = flip ? 100 - split : split;
  const beginSwipe = event => {
    if (!sourceAvailable || !stackRef.current) return;
    event.preventDefault();
    const element = stackRef.current;
    element.setPointerCapture(event.pointerId);
    const move = next => {
      const rect = element.getBoundingClientRect();
      const position = Math.max(0, Math.min(100, (next.clientX - rect.left) / Math.max(1, rect.width) * 100));
      setSplit(flip ? 100 - position : position);
    };
    move(event);
    const stop = () => {element.removeEventListener('pointermove', move);element.removeEventListener('lostpointercapture', stop);};
    element.addEventListener('pointermove', move);element.addEventListener('lostpointercapture', stop);
  };
  const blockedDifference = () => {
    setDifferenceNote('Difference unavailable: canvas access was blocked; showing Fade instead.');
    setMode('fade');
  };
  const chooseMode = next => {setDifferenceNote('');setMode(next);};
  const commonLayer = {gridArea: '1 / 1', minWidth: 0, width: '100%', alignSelf: 'start'};
  const sourceOnTop = flip && sourceAvailable;
  const sourceStyle = {...commonLayer, zIndex: sourceOnTop ? 2 : 1,
    opacity: sourceOnTop && mode === 'fade' ? shownBlend / 100 : 1,
    clipPath: sourceOnTop && mode === 'swipe' ? 'inset(0 0 0 ' + shownSplit + '%)' : 'none'};
  const builtStyle = {...commonLayer, zIndex: sourceOnTop ? 1 : 2, display: sourceAvailable ? 'block' : 'none',
    opacity: !sourceOnTop && mode === 'fade' ? shownBlend / 100 : 1,
    clipPath: !sourceOnTop && mode === 'swipe' ? 'inset(0 0 0 ' + shownSplit + '%)' : 'none'};

  return <div ref={surfaceRef} data-rw-visual-diff="1" data-flip={flip ? 'source' : 'built'} className="w-vd-surface">
    <div className="w-vd-bar">
      <RwDiffTabs mode={mode} setMode={chooseMode} />
      {(mode === 'fade' || mode === 'swipe') && <label className="w-blend"><span>Source</span>
        <input className="w-range" aria-label={mode === 'fade' ? 'Fade blend' : 'Swipe position'} type="range" min="0" max="100"
          value={mode === 'fade' ? shownBlend : split} disabled={!sourceAvailable}
          onChange={event => mode === 'fade' ? setBlend(flip ? 100 - +event.target.value : +event.target.value) : setSplit(+event.target.value)} />
        <span>Built</span></label>}
      <button className={'w-chip' + (flip ? ' on' : '')} disabled={!sourceAvailable} data-action="diff-flip"
        onPointerDown={() => setFlip(true)} onPointerUp={() => setFlip(false)} onPointerLeave={() => setFlip(false)}
        onPointerCancel={() => setFlip(false)}
        title="Hold to swap the two images — the fastest way to see what moved (hold F)"><BI.retry size={11} /> Flip</button>
      <span className="w-grow" style={{flex: 1}}></span>
      <span className="w-vd-px" data-pixel-match="unmeasured" title="No revision-bound pixel-match measurement is available">not measured<i>match</i></span>
      {props.srcImg && <button className="w-chip" onClick={() => props.setSrcImg?.(null)} title={props.srcImg.name + ' · local comparison only'}><BI.x size={11} /> Source</button>}
    </div>
    {!sourceAvailable && sourceStatus !== 'checking' && mode !== 'side' && <div className="w-vd-need" data-source-required>
      <BI.warn size={14}/><span>Drop a source design to use {RW_DIFF_MODES.find(entry => entry.key === mode).label.toLowerCase()} — there is nothing to compare against yet.</span>
    </div>}
    {differenceNote && <div className="w-vd-need" data-difference-fallback="fade"><BI.warn size={14}/><span>{differenceNote}</span></div>}
    {mode === 'difference' && differenceWhy && <div className="w-vd-need" data-difference-unavailable><BI.warn size={14}/><span>{differenceWhy}</span></div>}
    {!unrendered && !exactRetained && retainedWidths.length > 0 && <div className="w-retained-widths" data-retained-width-actions><span>Compare a measured width:</span>{retainedWidths.map(value => <button type="button" className="w-chip" key={value} disabled={typeof props.setVp !== 'function'} onClick={() => props.setVp?.({ ...props.vp, mode: value < 500 ? 'mobile' : 'desktop', [value < 500 ? 'mobile' : 'desktop']: value })}>{value}px</button>)}</div>}
    {mode === 'side' ? <div className="w-field pair" data-diff-panel="side">
      <div className="w-col" data-diff-pane="built" style={{order: sourceOnTop ? 2 : 1}}>
        <span className="w-col-h"><i className="w-dot" style={{background: 'var(--running)'}}></i>{props.draftDirty ? 'Local draft (unsaved)' : 'Built render'}{props.stagedPreview ? ' · staged patches' : ''}</span>
        <div className="w-vd-hold">{built()}<button className="w-vd-enlarge" aria-label="Enlarge built render" onClick={() => setLightbox('built')} /></div>
      </div>
      <div className="w-col" data-diff-pane="source" style={{order: sourceOnTop ? 1 : 2}}>
        <span className="w-col-h"><i className="w-dot" style={{background: 'var(--faint)'}}></i>Source design{props.srcImg ? ' · ' + props.srcImg.name : ''}</span>
        <div className="w-vd-hold">{source()}{sourceAvailable && <button className="w-vd-enlarge" aria-label="Enlarge source design" onClick={() => setLightbox('source')} />}</div>
      </div>
    </div> : <div className="w-field" data-diff-panel={mode}>
      {mode === 'difference' ? <div className="w-ov w-vd-pixels" ref={differenceRef} style={{width:stackWidth}}>
        <div style={{gridArea:'1 / 1'}}>{source()}</div>
        {diffAvailable && <React.Fragment>
          <div style={{gridArea:'1 / 1',visibility:'hidden'}}>{retained()}</div>
          <RwDifferenceCanvas rootRef={differenceRef} canvasRef={canvasRef} available={diffAvailable} hidden={flip}
            redrawKey={width + ':' + (retainedUrl || '') + ':' + sourceUrl + ':' + (props.srcImg?.url || '')} onBlocked={blockedDifference}/>
        </React.Fragment>}
      </div> : <div className={'w-ov w-vd-' + mode + (flip ? ' flip' : '')} ref={stackRef}
        onPointerDown={mode === 'swipe' ? beginSwipe : undefined} style={{width:stackWidth,display:'grid',alignItems:'start'}}>
        <div data-diff-layer="source" style={sourceStyle}>{source()}</div>
        <div data-diff-layer="built" style={builtStyle}>{built()}</div>
        {mode === 'swipe' && sourceAvailable && <div className="w-vd-handle" data-swipe-handle="1" aria-hidden="true" style={{left:shownSplit + '%'}}>
          <i></i><span><BI.chevron size={11} style={{transform:'rotate(180deg)'}}/><BI.chevron size={11}/></span>
        </div>}
      </div>}
    </div>}
    <div className="w-vd-keys"><span><kbd>1</kbd>–<kbd>4</kbd> mode</span><span><kbd>F</kbd> hold to flip</span>
      {mode === 'swipe' && <span><kbd>←</kbd><kbd>→</kbd> nudge divider</span>}
      {mode === 'side' && <span>click a pane to enlarge</span>}
      {mode === 'difference' && <span>black means identical</span>}
    </div>
    {lightbox && <RwDiffLightbox caption={lightbox === 'built' ? (props.draftDirty ? 'Local draft (unsaved)' : 'Built render') + ' · ' + width + 'px' : 'Source design'} onClose={() => setLightbox(null)}>
      <div style={{width:Math.min(width,900),maxWidth:'100%'}}>{lightbox === 'built' ? built(false) : source()}</div>
    </RwDiffLightbox>}
  </div>;
}

function DiffFull({ onClose = () => {}, ...props }) {
  React.useEffect(() => {
    const key = event => {if (event.key === 'Escape') onClose();};
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [onClose]);
  const width = props.vp?.mode === 'mobile' ? props.vp.mobile : props.vp?.desktop;
  return <div className="w-vdf w-app" data-diff-fullscreen-portal="1" role="dialog" aria-modal="true" aria-label="Visual diff">
    <header className="w-vdf-h"><span className="w-vdf-t"><BI.image size={16} style={{color:'var(--brand-orange)'}}/> Visual diff</span>
      <span className="w-meta">{props.job?.name || props.job?.id} · {width}px</span><span className="w-grow" style={{flex:1}}></span>
      <span className="w-meta">esc closes</span><button className="btn btn-ghost btn-sm" onClick={onClose}><BI.shrink size={14}/> Close</button>
    </header>
    <div className="w-vdf-b"><RwVisualDiffSurface {...props}/></div>
  </div>;
}

function RwVisualDiff(props) {
  const [mode,setMode] = React.useState('side');
  const [blend,setBlend] = React.useState(55);
  const [split,setSplit] = React.useState(50);
  const controls = {mode,setMode,blend,setBlend,split,setSplit};
  return props.full ? ReactDOM.createPortal(<DiffFull {...props} controls={controls} onClose={props.onCloseFull}/>,document.body)
    : <RwVisualDiffSurface {...props} controls={controls}/>;
}

Object.assign(window, { RwVisualDiff, DiffFull, RwRenderWidthEvidence });
